import { useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import case1 from '@/assets/images/case1.webp'
import case2 from '@/assets/images/case2.webp'
import case3 from '@/assets/images/case3.webp'
import case4 from '@/assets/images/case4.webp'
import case5 from '@/assets/images/case5.webp'
import case6 from '@/assets/images/case6.webp'
import case7 from '@/assets/images/case7.webp'

const cases = [
  {
    id: 1,
    image: case1,
    featuredImage: case7,
    title: 'Becoming the Technical Backbone',
    description:
      'Behind every growing product is a system quietly keeping operations stable, teams aligned, and technology evolving continuously as business requirements, scale, and technical complexity increase over time.',
    label: 'Becoming the\nTechnical Backbone',
  },
  {
    id: 2,
    image: case2,
    title: 'Scaling Dispatch Operations',
    description:
      'We rebuilt the dispatch infrastructure from the ground up, enabling real-time coordination across hundreds of moving parts while keeping latency low and reliability high.',
    label: 'Scaling\nDispatch Operations',
  },
  {
    id: 3,
    image: case3,
    title: 'Growing Beyond Initial Builds',
    description:
      'What starts as an MVP needs a clear path to scale. We helped this team evolve their architecture, tooling, and processes to support rapid growth without accumulating crippling technical debt.',
    label: 'Growing Beyond\nInitial Builds',
  },
  {
    id: 4,
    image: case4,
    title: 'Simplifying Creative Workflows',
    description:
      'Creative teams deserve tools that stay out of the way. We designed and built a streamlined workflow platform that cut handoff time in half and kept everyone in sync across projects.',
    label: 'Simplifying Creative\nWorkflows',
  },
  {
    id: 5,
    image: case5,
    title: 'Products Built for Practical Impact',
    description:
      'We focus on outcomes, not outputs. Every product decision was tied back to real user needs and measurable business results, not just feature checklists.',
    label: 'Products Built\nfor Practical Impact',
  },
  {
    id: 6,
    image: case6,
    title: 'Turning Ideas Into Action',
    description:
      'From early concept to shipped product, we compressed timelines without cutting corners, giving founders the clarity and momentum they needed to move fast and confidently.',
    label: 'Turning Ideas Into\nAction',
  },
]

export default function StudiesSection() {
  const [activeId, setActiveId] = useState(1)
  const scrollRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const dragMovedRef = useRef(false)
  const dragStartXRef = useRef(0)
  const dragStartScrollLeftRef = useRef(0)

  const active = cases.find((c) => c.id === activeId) ?? cases[0]

  const handlePointerDown = (e: React.MouseEvent) => {
    const el = scrollRef.current
    if (!el) return
    isDraggingRef.current = true
    dragMovedRef.current = false
    dragStartXRef.current = e.pageX
    dragStartScrollLeftRef.current = el.scrollLeft
  }

  const handlePointerMove = (e: React.MouseEvent) => {
    const el = scrollRef.current
    if (!el || !isDraggingRef.current) return
    const delta = e.pageX - dragStartXRef.current
    if (Math.abs(delta) > 5) dragMovedRef.current = true
    el.scrollLeft = dragStartScrollLeftRef.current - delta
  }

  const stopDragging = () => {
    isDraggingRef.current = false
  }

  const handleCardClick = (id: number) => {
    if (dragMovedRef.current) return
    setActiveId(id)
  }

  return (
    <section className="bg-white pt-16 sm:pt-24 xl:pt-[200px]" aria-labelledby="studies-heading">
      <div className="container-site">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-8 flex flex-col gap-3 sm:gap-4 lg:mb-10 lg:gap-6"
        >
          <span className="font-sans text-[13px] font-bold uppercase tracking-[0.061px] text-[#D4575B] sm:text-[15px] lg:text-[18px]">
            Blogs
          </span>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between lg:gap-6">
            <h2 className="max-w-[810px] font-sans text-[26px] font-medium leading-[30px] tracking-[-0.5px] sm:text-[34px] sm:leading-[38px] md:text-[40px] md:leading-[44px] lg:text-[48px] lg:leading-[55px] lg:tracking-[-1px]">
              <span className="text-[#2D2D2D]">Stories Behind the Systems.</span>{' '}
              <span className="text-[#979AC0]">Explore how ideas became real-world products.</span>
            </h2>
            <div className="shrink-0">
              <a
                href="/blog"
                className="inline-flex items-center justify-center gap-[10px] rounded-[10px] border border-[rgba(41,41,41,0.09)] bg-[#302F2F] p-2 md:p-4 text-center font-sans text-[16px] md:text-[18px] font-normal not-italic leading-normal text-white transition-colors hover:bg-black whitespace-nowrap"
                style={{ leadingTrim: 'both', textEdge: 'cap' } as React.CSSProperties}
              >
                Read more stories
              </a>
            </div>
          </div>
        </motion.div>

        {/* Main content */}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
          {/* Big card */}
          <div className="relative overflow-hidden rounded-[10px] w-full h-[220px] sm:h-[300px] md:h-[360px] lg:shrink-0 lg:w-[495px] lg:h-[404px] border border-[#E8E8E8]">
            <AnimatePresence mode="wait">
              <motion.img
                key={active.id}
                src={active.featuredImage ?? active.image}
                alt={`Case study illustration for: ${active.title}`}
                className="w-full h-full object-cover"
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.45, ease: 'easeInOut' }}
              />
            </AnimatePresence>
          </div>

          {/* Text block */}
          <div className="flex flex-col justify-between gap-5 lg:gap-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
                className="flex flex-col gap-3 lg:gap-5 w-full lg:max-w-[655px]"
              >
                <h3 className="font-sans text-[22px] sm:text-[26px] lg:text-[32px] font-medium leading-tight tracking-[-1px] text-[#2D2D2D]">
                  {active.title}
                </h3>
                <p className="font-sans text-[14px] sm:text-[15px] lg:text-[24px] leading-relaxed text-[#747474] tracking-[-1px]">
                  {active.description}
                </p>
              </motion.div>
            </AnimatePresence>
            <div className="shrink-0">
              <a
                href="#"
                className="inline-flex items-center justify-center gap-[10px] rounded-[10px] border border-[rgba(41,41,41,0.09)] bg-[#302F2F] p-3 text-center font-sans text-[16px] md:text-[18px] font-normal not-italic leading-normal text-white transition-colors hover:bg-black whitespace-nowrap"
                style={{ leadingTrim: 'both', textEdge: 'cap' } as React.CSSProperties}
              >
                Continue reading...
              </a>
            </div>
          </div>
        </div>

        {/* Scrollable small cards */}
        <div className="mt-6 sm:mt-8 relative">
          <div
            ref={scrollRef}
            className="flex cursor-grab gap-4 overflow-x-auto pb-4 scrollbar-hide active:cursor-grabbing"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={stopDragging}
            onMouseLeave={stopDragging}
          >
            {cases.map((item) => (
              <button
                key={item.id}
                onClick={() => handleCardClick(item.id)}
                className="relative shrink-0 overflow-hidden rounded-[10px] text-left transition-all duration-300 focus:outline-none w-[180px] h-[130px] sm:w-[220px] sm:h-[160px] md:w-[250px] md:h-[185px] lg:w-[271px] lg:h-[208px]"
              >
                <img
                  src={item.image}
                  alt={`Case study card for: ${item.title}`}
                  draggable={false}
                  className="w-full h-full object-cover"
                />
                {/* Gradient overlay */}
                <div
                  className="absolute inset-0 transition-opacity duration-300"
                  style={{
                    background:
                      'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)',
                  }}
                />
                {/* Active ring */}
                {activeId === item.id && (
                  <div className="absolute inset-0 rounded-[10px] ring-2 ring-white pointer-events-none" />
                )}
                {/* Label */}
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="font-sans text-[13px] font-medium leading-snug text-white whitespace-pre-line">
                    {item.label}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
